/**
 * Adaptive Assessment™ — shared data & educational content.
 *
 * Design intent:
 * - Educational, not diagnostic. Language must never imply lab measurement,
 *   clinical diagnosis or treatment.
 * - Every recommended INPUT carries a Purpose and Expected Adaptation so users
 *   and practitioners can see why the framework selects it.
 * - Educational modules explain the physiology behind the read, so a clinician
 *   can hand the profile back to a patient as an interpretation report.
 */

export const SECTIONS = [
  {
    id: "state",
    label: "01 · Current State",
    heading: "How is the system running right now?",
    dimensionLabel: "Current State",
    items: [
      {
        key: "energy",
        q: "How steady is your energy across a typical day?",
        scaleLow: "1 · Very unsteady",
        scaleHigh: "5 · Very steady",
      },
      {
        key: "stress",
        q: "How high is your overall stress right now?",
        scaleLow: "1 · Very low",
        scaleHigh: "5 · Very high",
        reverse: true,
      },
      {
        key: "emotion",
        q: "How well can you regulate your emotions when demands increase?",
        scaleLow: "1 · Not well",
        scaleHigh: "5 · Very well",
      },
      {
        key: "sleep",
        q: "How restorative does your sleep usually feel?",
        scaleLow: "1 · Not restorative",
        scaleHigh: "5 · Very restorative",
      },
      {
        key: "recovery",
        q: "How well do you recover after physical or emotional demand?",
        scaleLow: "1 · Not well",
        scaleHigh: "5 · Very well",
      },
      {
        key: "motivation",
        q: "How available is your motivation to engage with the day?",
        scaleLow: "1 · Not available",
        scaleHigh: "5 · Readily available",
      },
      {
        key: "clarity",
        q: "How clear and focused does your thinking feel?",
        scaleLow: "1 · Very unclear",
        scaleHigh: "5 · Very clear",
      },
      {
        key: "discomfort",
        q: "How much does physical discomfort or pain limit you?",
        scaleLow: "1 · Not at all",
        scaleHigh: "5 · A great deal",
        reverse: true,
      },
    ],
  },
  {
    id: "inputs",
    label: "02 · Current Inputs",
    heading: "What signals is your nervous system receiving?",
    dimensionLabel: "Current Inputs",
    items: [
      {
        key: "movement",
        q: "How consistently do you include unstructured daily movement, such as walking?",
        scaleLow: "1 · Rarely",
        scaleHigh: "5 · Consistently",
      },
      {
        key: "exercise",
        q: "How consistently do you complete structured aerobic or resistance exercise?",
        scaleLow: "1 · Rarely",
        scaleHigh: "5 · Consistently",
      },
      {
        key: "outdoors",
        q: "How consistently do you spend time outdoors in natural light?",
        scaleLow: "1 · Rarely",
        scaleHigh: "5 · Consistently",
      },
      {
        key: "nutrition",
        q: "How consistently does your nutrition support steady energy and recovery?",
        scaleLow: "1 · Rarely",
        scaleHigh: "5 · Consistently",
      },
      {
        key: "hydration",
        q: "How consistently do you stay adequately hydrated?",
        scaleLow: "1 · Rarely",
        scaleHigh: "5 · Consistently",
      },
      {
        key: "screens",
        q: "How consistently do you keep screen use within limits that support recovery?",
        scaleLow: "1 · Rarely",
        scaleHigh: "5 · Consistently",
      },
      {
        key: "connection",
        q: "How consistently do you have meaningful social connection?",
        scaleLow: "1 · Rarely",
        scaleHigh: "5 · Consistently",
      },
      {
        key: "routine",
        q: "How consistent is your daily and weekly routine?",
        scaleLow: "1 · Very inconsistent",
        scaleHigh: "5 · Very consistent",
      },
    ],
  },
  {
    id: "capacity",
    label: "03 · Physical Capacity",
    heading: "What is the body currently able to receive?",
    dimensionLabel: "Physical Capacity",
    items: [
      {
        key: "activity",
        q: "How well does your current activity level support your goals?",
        scaleLow: "1 · Not at all",
        scaleHigh: "5 · Very well",
      },
      {
        key: "injury",
        q: "How free are you from injury or physical limitation during movement?",
        scaleLow: "1 · Very limited",
        scaleHigh: "5 · Fully unrestricted",
      },
      {
        key: "confidence",
        q: "How confident do you feel moving and exercising?",
        scaleLow: "1 · Not confident",
        scaleHigh: "5 · Very confident",
      },
      {
        key: "tolerance",
        q: "How well can you tolerate your current exercise intensity and duration?",
        scaleLow: "1 · Not well",
        scaleHigh: "5 · Very well",
      },
    ],
  },
];

export const BEHAVIORS = [
  { key: "withdrawal", label: "Withdrawal — pulling back from people or life" },
  {
    key: "avoidance",
    label: "Avoidance — putting things off, avoiding the difficult",
  },
  { key: "scrolling", label: "Scrolling — extended time on phone or feeds" },
  { key: "substance", label: "Substance use — alcohol, cannabis, other" },
  { key: "eating", label: "Emotional eating — food as regulation" },
  { key: "overwork", label: "Overworking — using productivity to avoid state" },
  { key: "exercise", label: "Exercise — as your primary regulator" },
  {
    key: "connection",
    label: "Reaching out — social connection when stressed",
  },
];

export const CURRENT_STATE_OPTIONS = [
  {
    key: "angry-irritable",
    label: "Angry or irritable",
    tone: "High activation",
  },
  {
    key: "anxious-overwhelmed",
    label: "Anxious or overwhelmed",
    tone: "High demand",
  },
  {
    key: "restless-activated",
    label: "Restless or activated",
    tone: "Mobilized",
  },
  { key: "sad-low", label: "Sad or low", tone: "Lower mood" },
  {
    key: "numb-withdrawn",
    label: "Numb or withdrawn",
    tone: "Reduced engagement",
  },
  { key: "depleted", label: "Depleted", tone: "Low available energy" },
  { key: "calm-steady", label: "Calm or steady", tone: "Working range" },
  {
    key: "positive-energized",
    label: "Positive or energized",
    tone: "Available capacity",
  },
];

export const CURRENT_URGE_OPTIONS = [
  { key: "react", label: "React quickly or confront the problem" },
  { key: "withdraw", label: "Pull away or shut down" },
  { key: "seek-relief", label: "Reach for familiar relief or stimulation" },
  { key: "move", label: "Move, pace, or discharge physical energy" },
  { key: "connect", label: "Reach out to someone" },
  { key: "none", label: "No clear urge or unsure" },
];

export const PHYSICAL_READINESS_OPTIONS = [
  {
    key: "unrestricted",
    label: "Ready for my usual movement",
    detail:
      "I can perform familiar light, moderate, or vigorous activity with control.",
  },
  {
    key: "light-moderate",
    label: "Ready for light-to-moderate movement",
    detail:
      "I can move, but vigorous effort would not be appropriate right now.",
  },
  {
    key: "modify",
    label: "Movement needs modification",
    detail:
      "Pain, injury, disability, or another limitation changes what I can safely perform.",
  },
  {
    key: "pause",
    label: "Exercise should wait",
    detail:
      "I have a new or unexplained symptom, feel unwell, or have been told to seek guidance before exercise.",
  },
];

export const HYDRATION_OPTIONS = [
  { key: "ready", label: "Adequately hydrated" },
  { key: "some", label: "Some fluids, but probably not enough" },
  { key: "little", label: "Very little fluid today" },
  { key: "unsure", label: "Unsure" },
];

export const NOURISHMENT_OPTIONS = [
  { key: "ready", label: "Adequately nourished for today" },
  { key: "light", label: "Only light intake so far" },
  { key: "not-recent", label: "I have not eaten recently" },
  { key: "unsure", label: "Unsure" },
];

export const AVAILABILITY_OPTIONS = [
  { key: "none", label: "No reliable time", minutes: 0 },
  { key: "2-5", label: "2–5 minutes", minutes: 3 },
  { key: "5-10", label: "5–10 minutes", minutes: 7 },
  { key: "10-20", label: "10–20 minutes", minutes: 12 },
  { key: "20-30", label: "20–30 minutes", minutes: 20 },
  { key: "30-plus", label: "30+ minutes", minutes: 30 },
];

export const DAY_SLOTS = [
  { key: "start", label: "Start of day", defaultTime: "08:00" },
  { key: "middle", label: "Middle of day", defaultTime: "12:30" },
  { key: "end", label: "End of day", defaultTime: "18:30" },
];

export const STRESS_WINDOW_OPTIONS = [
  {
    key: "before-day",
    label: "Before work, school, or daily responsibilities",
  },
  { key: "morning", label: "Morning" },
  { key: "midday", label: "Middle of the day" },
  { key: "late-day", label: "Late afternoon" },
  { key: "transition", label: "Transition home or out of work/school" },
  { key: "evening", label: "Evening" },
  { key: "unpredictable", label: "It is unpredictable" },
];

export const RECOVERY_WINDOW_OPTIONS = [
  { key: "morning", label: "Morning" },
  { key: "midday", label: "Middle of the day" },
  { key: "transition", label: "Transition home or out of work/school" },
  { key: "evening", label: "Evening" },
  { key: "bedtime", label: "Near bedtime" },
  { key: "unpredictable", label: "It is unpredictable" },
];

export const WEEKDAY_OPTIONS = [
  { key: "MO", label: "Mon" },
  { key: "TU", label: "Tue" },
  { key: "WE", label: "Wed" },
  { key: "TH", label: "Thu" },
  { key: "FR", label: "Fri" },
  { key: "SA", label: "Sat" },
  { key: "SU", label: "Sun" },
];

export const SOCIAL_PREFERENCE_OPTIONS = [
  { key: "alone", label: "Quiet movement on my own" },
  { key: "trusted-person", label: "Movement or exercise with someone" },
  { key: "either", label: "Either, depending on the day" },
  { key: "unsure", label: "Unsure" },
];

export const RESOURCE_OPTIONS = [
  { key: "outside", label: "Outdoor access" },
  { key: "safe-walk", label: "Walking route" },
  { key: "indoor-space", label: "Indoor movement space" },
  { key: "gym", label: "Gym or recreation centre" },
  { key: "equipment", label: "Exercise equipment" },
  { key: "stairs", label: "Stairs or an incline" },
  { key: "water", label: "Pool or water access" },
  { key: "grass-yard", label: "Grass, yard, or open space" },
  { key: "trusted-person", label: "Someone who may join me" },
  { key: "sport-group", label: "Sport, class, or activity group" },
  { key: "none", label: "None of these right now" },
];

export const EXERCISE_FAMILIARITY_OPTIONS = [
  { key: "aerobic", label: "Continuous aerobic exercise" },
  { key: "resistance", label: "Resistance training" },
  { key: "anaerobic", label: "Short intervals or anaerobic exercise" },
  { key: "sport-competition", label: "Sport or competitive activity" },
  { key: "none-unsure", label: "None or unsure" },
];

export const PREFERRED_ACTIVITY_OPTIONS = [
  { key: "archery", label: "Archery" },
  { key: "badminton", label: "Badminton" },
  { key: "baseball", label: "Baseball" },
  { key: "basketball", label: "Basketball" },
  { key: "beach-volleyball", label: "Beach volleyball" },
  { key: "biking", label: "Biking or cycling" },
  { key: "billiards", label: "Billiards or pool" },
  { key: "bocce", label: "Bocce" },
  { key: "bowling", label: "Bowling" },
  { key: "boxing", label: "Boxing" },
  { key: "brazilian-jiu-jitsu", label: "Brazilian jiu-jitsu" },
  { key: "broomball", label: "Broomball" },
  { key: "cheerleading", label: "Cheerleading" },
  { key: "cornhole", label: "Cornhole" },
  { key: "cricket", label: "Cricket" },
  { key: "cross-country-skiing", label: "Cross-country skiing" },
  { key: "curling", label: "Curling" },
  { key: "dancing", label: "Dance" },
  { key: "darts", label: "Darts" },
  { key: "disc-golf", label: "Disc golf" },
  { key: "dodgeball", label: "Dodgeball" },
  { key: "field-hockey", label: "Field hockey" },
  { key: "figure-skating", label: "Figure skating" },
  { key: "flag-football", label: "Flag football" },
  { key: "floor-hockey", label: "Floor hockey" },
  { key: "football", label: "Football" },
  { key: "golf", label: "Golf" },
  { key: "group-exercise", label: "Group exercise or fitness class" },
  { key: "gymnastics", label: "Gymnastics" },
  { key: "hiking", label: "Hiking" },
  { key: "horseshoes", label: "Horseshoes" },
  { key: "ice-hockey", label: "Ice hockey" },
  { key: "judo", label: "Judo" },
  { key: "karate", label: "Karate" },
  { key: "kickball", label: "Kickball" },
  { key: "lacrosse", label: "Lacrosse" },
  { key: "lawn-games", label: "Lawn games" },
  { key: "martial-arts", label: "Martial arts — other" },
  { key: "mini-golf", label: "Mini golf" },
  { key: "mma", label: "Mixed martial arts (MMA)" },
  { key: "muay-thai", label: "Muay Thai" },
  { key: "pickleball", label: "Pickleball" },
  { key: "racquetball", label: "Racquetball" },
  { key: "ringette", label: "Ringette" },
  { key: "rowing", label: "Rowing" },
  { key: "rugby", label: "Rugby" },
  { key: "running", label: "Running" },
  { key: "skateboarding", label: "Skateboarding" },
  { key: "skiing", label: "Skiing" },
  { key: "snowboarding", label: "Snowboarding" },
  { key: "soccer", label: "Soccer" },
  { key: "softball", label: "Softball" },
  { key: "squash", label: "Squash" },
  { key: "strength", label: "Strength training or weightlifting" },
  { key: "swimming", label: "Swimming" },
  { key: "table-tennis", label: "Table tennis" },
  { key: "taekwondo", label: "Taekwondo" },
  { key: "tennis", label: "Tennis" },
  { key: "track-field", label: "Track and field" },
  { key: "ultimate-frisbee", label: "Ultimate Frisbee" },
  { key: "volleyball", label: "Volleyball" },
  { key: "walking", label: "Walking or walking group" },
  { key: "water-polo", label: "Water polo" },
  { key: "wrestling", label: "Wrestling" },
  { key: "yoga", label: "Yoga" },
  { key: "other", label: "Another activity" },
  { key: "none", label: "None currently" },
];

const COMPETITIVE_ACTIVITY_KEYS = PREFERRED_ACTIVITY_OPTIONS.filter(
  (option) =>
    ![
      "billiards",
      "cornhole",
      "darts",
      "horseshoes",
      "lawn-games",
      "mini-golf",
      "walking",
      "yoga",
      "other",
      "none",
    ].includes(option.key),
).map((option) => option.key);

const RECREATIONAL_ACTIVITY_KEYS = PREFERRED_ACTIVITY_OPTIONS.filter(
  (option) => option.key !== "none",
).map((option) => option.key);

const SOCIAL_ACTIVITY_KEYS = [
  "badminton",
  "basketball",
  "beach-volleyball",
  "biking",
  "billiards",
  "bocce",
  "bowling",
  "cornhole",
  "dancing",
  "darts",
  "disc-golf",
  "dodgeball",
  "flag-football",
  "golf",
  "group-exercise",
  "hiking",
  "horseshoes",
  "kickball",
  "lawn-games",
  "mini-golf",
  "pickleball",
  "softball",
  "table-tennis",
  "tennis",
  "ultimate-frisbee",
  "volleyball",
  "walking",
  "yoga",
  "other",
];

export const ACTIVITY_SELECTION_GROUPS = [
  {
    key: "competitive",
    label: "Competitive sports and activities",
    description: "Organized training, leagues, tournaments, or competition.",
    optionKeys: COMPETITIVE_ACTIVITY_KEYS,
  },
  {
    key: "recreational",
    label: "Recreational sports and activities",
    description:
      "Activities you do for health, enjoyment, practice, or recreation.",
    optionKeys: RECREATIONAL_ACTIVITY_KEYS,
  },
  {
    key: "social",
    label: "Social or casual games and activities",
    description: "Low-pressure activities you may do with other people.",
    optionKeys: SOCIAL_ACTIVITY_KEYS,
  },
];

export const ACTIVITY_INTENT_OPTIONS = [
  { key: "begin", label: "I would like to begin" },
  { key: "more-often", label: "I would like to participate more often" },
  { key: "regular", label: "I already participate regularly" },
  { key: "unsure-start", label: "I am interested but unsure how to start" },
  { key: "not-now", label: "Not right now" },
];

export const SCHEDULED_PARTICIPATION_OPTIONS = [
  { key: "scheduled", label: "Yes, I have regular or scheduled activity" },
  { key: "none", label: "No scheduled activity right now" },
];

export const ACTIVITY_EFFORT_OPTIONS = [
  { key: "light", label: "Light" },
  { key: "moderate", label: "Moderate" },
  { key: "high", label: "High" },
];

export const WARMUP_RECOVERY_OPTIONS = [
  { key: "both", label: "I already warm up and cool down" },
  { key: "warmup", label: "I warm up but do not usually cool down" },
  { key: "cooldown", label: "I cool down but do not usually warm up" },
  { key: "neither", label: "I do not usually do either" },
];

export const COMPETITION_RESPONSE_OPTIONS = [
  { key: "focus-engage", label: "Helps me focus and engage" },
  {
    key: "motivates-overactivates",
    label: "Motivates me but can overactivate me",
  },
  {
    key: "pressure-discouraging",
    label: "Usually feels pressuring or discouraging",
  },
  { key: "little-effect", label: "Has little effect" },
  { key: "unsure", label: "Unsure or insufficient experience" },
];

export const STEPS = [
  "welcome",
  "state",
  "inputs",
  "capacity",
  "behaviors",
  "schedule",
  "results",
];

// -------------------------------------------------------------------------
// INPUT LIBRARY — each recommended input carries Purpose + Expected Adaptation.
// Referenced by archetypes below via key.
// -------------------------------------------------------------------------
export const INPUT_LIBRARY = {
  "zone2-aerobic": {
    title: "Zone 2 aerobic base",
    purpose: [
      "Increase parasympathetic influence",
      "Support recovery without overshooting",
      "Reduce sustained sympathetic load",
    ],
    expectedAdaptation: [
      "Wider recovery capacity",
      "Improved sleep depth",
      "Greater emotional flexibility under stress",
    ],
  },
  "respiratory-pacing": {
    title: "Structured breath / respiratory pacing",
    purpose: [
      "Support a calming, paced-breathing regulation pattern",
      "Interrupt high-activation feedback loops",
      "Provide a repeatable regulation input",
    ],
    expectedAdaptation: [
      "Faster self-reported settling after demand",
      "Reduced reactivity under demand",
      "More reliable access to calm",
    ],
  },
  "sleep-protocol": {
    title: "Non-negotiable sleep protocol",
    purpose: [
      "Consolidate autonomic recovery",
      "Support hormonal rhythm (cortisol, growth hormone)",
      "Enable emotional and memory processing",
    ],
    expectedAdaptation: [
      "Deeper restorative sleep",
      "Steadier morning energy",
      "Better daytime regulation",
    ],
  },
  "daily-downshift": {
    title: "Deliberate downshifting rhythm (daily)",
    purpose: [
      "Teach the system that recovery is safe",
      "Prevent full-day sympathetic drift",
      "Interrupt chronic vigilance patterns",
    ],
    expectedAdaptation: [
      "Improved recovery access on demand",
      "Reduced background activation",
      "Wider window of tolerance",
    ],
  },
  "daily-walks": {
    title: "Short daily walks — start unambitious",
    purpose: [
      "Introduce a low-dose, high-consistency input",
      "Support basic circulatory + metabolic function",
      "Rebuild movement identity without overshooting capacity",
    ],
    expectedAdaptation: [
      "Improved mood and daylight exposure",
      "Modest gains in aerobic base",
      "Confidence to build the next input",
    ],
  },
  "nutrition-basics": {
    title: "Simple nutrition and hydration structure",
    purpose: [
      "Stabilize blood glucose and energy signalling",
      "Support neurotransmitter and hormone production",
      "Reduce metabolic contribution to fatigue",
    ],
    expectedAdaptation: [
      "Steadier mood and energy across the day",
      "Better recovery from load",
      "Fewer regulatory swings",
    ],
  },
  "foundational-movement": {
    title: "Foundational movement before intensity",
    purpose: [
      "Rebuild joint tolerance and motor confidence",
      "Restore basic capacity before adding stimulus",
      "Reduce injury risk in a narrowed system",
    ],
    expectedAdaptation: [
      "Increased tolerance for future load",
      "Reduced pain / limitation signals",
      "Physical confidence returns",
    ],
  },
  "vagal-loading": {
    title: "Respiratory / recovery-oriented breathing practice",
    purpose: [
      "Practice breath patterns often discussed with recovery education",
      "Support steadier self-reported recovery between stresses",
      "Widen access to recovery states",
    ],
    expectedAdaptation: [
      "Steadier self-reported recovery between stresses",
      "Faster post-stress recovery",
      "More stable regulation under demand",
    ],
  },
  "circadian-consistency": {
    title: "Circadian consistency (light + timing)",
    purpose: [
      "Use morning light and daily timing to support a steadier day–night rhythm",
      "Support evening wind-down habits that aid sleep readiness",
      "Align feeding, activity and rest windows",
    ],
    expectedAdaptation: [
      "More reliable sleep onset and depth",
      "Clearer morning activation",
      "Reduced evening dysregulation",
    ],
  },
  "environmental-design": {
    title: "Environmental design + rhythm",
    purpose: [
      "Remove friction from wanted inputs",
      "Reduce stimulus load from unwanted inputs",
      "Let the environment do the regulation work",
    ],
    expectedAdaptation: [
      "Inputs happen with less willpower",
      "Fewer high-friction failure points",
      "Reliable daily rhythm emerges",
    ],
  },
  "interoceptive-practice": {
    title: "Interoceptive practice",
    purpose: [
      "Rebuild accurate reading of internal state",
      "Increase distance between state and reaction",
      "Give the system language for its own signals",
    ],
    expectedAdaptation: [
      "Earlier detection of state shifts",
      "Better matched inputs to actual need",
      "Reduced surprise reactivity",
    ],
  },
  "behavioral-substitutions": {
    title: "Behavioral substitutions for stress patterns",
    purpose: [
      "Replace an existing regulator with a lower-cost one",
      "Preserve the regulation function while improving the vector",
      "Avoid willpower-only extinction",
    ],
    expectedAdaptation: [
      "Old patterns lose salience",
      "New regulators become default",
      "Recovery no longer competes with stress relief",
    ],
  },
  "hrv-guided-training": {
    title: "Readiness-matched training load",
    purpose: [
      "Match daily load to how recovered you feel today",
      "Reduce the chance of training past current tolerance",
      "Optimize the adaptation-to-cost ratio",
    ],
    expectedAdaptation: [
      "Higher ceiling with lower recovery cost",
      "Reduced overreaching",
      "More sustainable progression",
    ],
  },
  "recovery-protocols": {
    title: "Deliberate recovery protocols",
    purpose: [
      "Give recovery the same intentionality as training",
      "Consolidate adaptations between doses",
      "Protect recovery capacity between demanding sessions",
    ],
    expectedAdaptation: [
      "Faster between-session recovery",
      "Reduced accumulated fatigue",
      "Higher expressed performance",
    ],
  },
  "attention-training": {
    title: "Attention & performance under load",
    purpose: [
      "Train attentional control in physiologically demanding states",
      "Widen the state where output remains available",
      "Expand what the system can hold under demand",
    ],
    expectedAdaptation: [
      "Better decision quality under stress",
      "Steadier performance late in load",
      "Reduced performance drop-off",
    ],
  },
  "deload-cadence": {
    title: "Structured deload cadence",
    purpose: [
      "Prevent cumulative sympathetic drift",
      "Preserve long-arc progression",
      "Protect long-term capacity",
    ],
    expectedAdaptation: [
      "Sustainable performance over months",
      "Reduced injury and burnout risk",
      "Stronger adaptation waves",
    ],
  },
};

// -------------------------------------------------------------------------
// ADAPTIVE PHYSIOLOGY FLOW — 8-node signature diagram (also used as data
// source for the visual component).
// -------------------------------------------------------------------------
export const ADAPTIVE_FLOW = [
  {
    id: "input",
    label: "Input",
    subtitle: "A signal the system receives",
    icon: "arrow-down-to-line",
  },
  {
    id: "state",
    label: "Physiological State",
    subtitle: "How the system is currently running",
    icon: "activity",
  },
  {
    id: "nervous",
    label: "Nervous System",
    subtitle: "Sympathetic / parasympathetic tone",
    icon: "waves",
  },
  {
    id: "hormonal",
    label: "Hormonal Environment",
    subtitle: "Cortisol, adrenaline, insulin, others",
    icon: "flask-conical",
  },
  {
    id: "behavior",
    label: "Behavior",
    subtitle: "The observable output",
    icon: "sparkles",
  },
  {
    id: "prescribed",
    label: "Suggested Inputs",
    subtitle: "What the framework offers next",
    icon: "compass",
  },
  {
    id: "adaptation",
    label: "Adaptation",
    subtitle: "The system reorganises",
    icon: "refresh-cw",
  },
  {
    id: "baseline",
    label: "New Baseline",
    subtitle: "The set-point widens",
    icon: "trending-up",
  },
];

// -------------------------------------------------------------------------
// ARCHETYPES — extended with `environment`, `feelings`, `inputs`.
// -------------------------------------------------------------------------
export const ARCHETYPES = {
  "high-activation": {
    key: "high-activation",
    title: "High Activation",
    subtitle: "The system is running hot.",
    body: "Your responses describe a high-arousal pattern: elevated stress load, narrowed recovery, and behaviour patterns that tend to keep the system engaged. Nothing about this is a personal failing; it is a common adaptive response to sustained demand.",
    principles: [
      "Physiological state influences behavior.",
      "The nervous system adapts to what it receives.",
    ],
    loopStage: "State",
    loopHighlight: "state",
    flowHighlight: "nervous",
    frameworkStep: "01 · Assess the State (recovery markers)",
    environment: {
      nervousSystemState:
        "Your self-reported emotional, behavioural, and physical patterns are consistent with heightened sympathetic activation—the state a healthy body often recruits under sustained perceived demand.",
      priorities: [
        "Maintaining readiness and rapid response",
        "Prioritising threat scanning over recovery",
        "Preserving energy for immediate action over long-arc repair",
        "Narrowing attention to what feels urgent",
      ],
      behavioralOutputs: [
        "Difficulty settling — mind ahead of body",
        "Reactivity that surprises you afterwards",
        "Sleep that doesn't feel restorative",
        "Reduced tolerance for ambiguity or delay",
      ],
      recoveryChallenges: [
        "The system may not yet trust recovery — it can feel unpleasant before it feels helpful",
        "Sleep architecture is often compressed even at full duration",
        "Attempts at stillness may provoke restlessness before they produce calm",
      ],
      whyThisMatters:
        "Educational context: sustained sympathetic activation is adaptive short-term but can cost long-term recovery. Adrenaline (epinephrine) is commonly discussed in relation to acute activation; if activation remains prolonged, cortisol is often discussed as increasingly relevant. Self-reported patterns commonly include behaviours that keep the system engaged — a loop that inputs, not willpower, are well positioned to interrupt.",
    },
    feelings: [
      "Feeling wired but tired",
      "Impatience with small friction",
      "Difficulty concentrating on non-urgent work",
      "Emotional reactivity that surprises you later",
      "Restlessness — inability to be still",
      "Increased cravings (stimulants, sugar, alcohol)",
      "Reduced motivation for slower recovery behaviours",
    ],
    inputs: [
      "zone2-aerobic",
      "respiratory-pacing",
      "sleep-protocol",
      "daily-downshift",
    ],
    applications: [
      { label: "Stress & Burnout", to: "/applications/stress-burnout" },
      { label: "Anxiety & Regulation", to: "/applications/anxiety-regulation" },
    ],
    programs: [
      { label: "Foundations", to: "/programs/foundations" },
      { label: "Guided Implementation", to: "/programs/guided-implementation" },
    ],
  },
  "reduced-capacity": {
    key: "reduced-capacity",
    title: "Reduced Capacity",
    subtitle: "The system needs to widen before it can raise.",
    body: "Your responses describe reduced energy, motivation, and physical tolerance. The first priority is accessible, repeatable movement that builds activation, confidence, and capacity.",
    principles: [
      "Repeated inputs create adaptations.",
      "Intentional inputs create new adaptations.",
    ],
    loopStage: "Input",
    loopHighlight: "input",
    flowHighlight: "prescribed",
    frameworkStep: "02 · Match the Activity (dosed to current tolerance)",
    environment: {
      nervousSystemState:
        "Your self-reported emotional, behavioural, and physical patterns are consistent with a narrower receiving capacity, so inputs may need to remain small and repeatable before they are intensified.",
      priorities: [
        "Preserving remaining energy and motivation",
        "Avoiding overshoot signals (fatigue, pain, disengagement)",
        "Restoring baseline function before ambition",
        "Rebuilding trust in the body's ability to respond",
      ],
      behavioralOutputs: [
        "Starting things without finishing them",
        "Difficulty initiating even wanted activity",
        "Preferring passive recovery over active input",
        "Motivation that arrives but doesn't sustain",
      ],
      recoveryChallenges: [
        "Recovery windows may not feel productive, even when they are",
        "Small inputs can feel dismissive of a bigger problem",
        "Progress is often visible weeks before it feels present",
      ],
      whyThisMatters:
        "A narrowed capacity interprets new load as threat rather than signal. Any input above tolerance produces regression, not adaptation. This is why the framework suggests wider before higher — building the receiving surface is the first job. Educational topics such as stress-hormone rhythm, sleep quality, and metabolic signalling are often discussed alongside consistent low-dose inputs before intensity is added — without claiming those were measured here.",
    },
    feelings: [
      "Low baseline energy that no rest quite fixes",
      "Wanting to move but not being able to start",
      "Emotional flatness or reduced motivation",
      "Feeling defeated by inputs that used to be manageable",
      "Guilt about the size of a starting dose",
      "Fatigue that gets worse with big attempts",
    ],
    inputs: [
      "daily-walks",
      "nutrition-basics",
      "sleep-protocol",
      "foundational-movement",
    ],
    applications: [
      { label: "Habit & Behavior Change", to: "/applications/habit-change" },
      { label: "Recovery", to: "/applications/recovery" },
    ],
    programs: [
      { label: "Foundations", to: "/programs/foundations" },
      { label: "Guided Implementation", to: "/programs/guided-implementation" },
    ],
  },
  "recovery-focus": {
    key: "recovery-focus",
    title: "Recovery Focus",
    subtitle: "Load is fine. Recovery isn't.",
    body: "Your responses describe inputs and capacity that look reasonable, with the recovery side of the loop comparatively under-resourced. The next adaptation comes from strengthening what happens between periods of demand.",
    principles: [
      "Recovery is when adaptation is consolidated.",
      "The state influences behavior.",
    ],
    loopStage: "Feedback",
    loopHighlight: "feedback",
    flowHighlight: "adaptation",
    frameworkStep: "03 · Reinforce the Signal (recovery cadence)",
    environment: {
      nervousSystemState:
        "Your self-reported patterns are consistent with loading that is outpacing recovery. Activation can remain higher than the training load alone would suggest when recovery is under-resourced.",
      priorities: [
        "Consolidating what the load has already asked for",
        "Restoring recovery capacity between doses",
        "Rebuilding sleep quality even if sleep duration is fine",
        "Preventing accumulation of sub-threshold fatigue",
      ],
      behavioralOutputs: [
        "Feeling flat despite doing 'all the right things'",
        "Sleep that is long but not restorative",
        "Motivation dip disproportionate to the input",
        "Irritability toward late-day demand",
      ],
      recoveryChallenges: [
        "Recovery is often mistaken for absence of load, not an active input",
        "How recovered you feel may lag even when training consistency looks fine",
        "Sleep may need quality and timing changes, not only more duration",
      ],
      whyThisMatters:
        "Adaptation is not consolidated during load — it is consolidated between doses. When recovery is under-resourced, additional load compounds cost rather than producing signal. Educational options often focus on restoring recovery access (paced breathing, sleep quality, circadian regularity) rather than adding more load.",
    },
    feelings: [
      "Doing everything right but feeling worse",
      "Sleep that doesn't cash in",
      "Emotional flatness on rest days",
      "Small demands producing outsized fatigue",
      "Loss of ordinary enthusiasm",
      "Slower recovery from workouts that used to be easy",
    ],
    inputs: [
      "sleep-protocol",
      "zone2-aerobic",
      "vagal-loading",
      "circadian-consistency",
    ],
    applications: [
      { label: "Stress & Burnout", to: "/applications/stress-burnout" },
      { label: "Performance", to: "/applications/performance" },
    ],
    programs: [
      { label: "Guided Implementation", to: "/programs/guided-implementation" },
      { label: "Foundations", to: "/programs/foundations" },
    ],
  },
  "regulation-building": {
    key: "regulation-building",
    title: "Regulation Building",
    subtitle: "The pattern is close. The regulation is next.",
    body: "Your responses describe inputs and capacity in the working range, with behaviour patterns that still have room to become more consistent. Repeatable movement and progressive challenge can strengthen regulation over time.",
    principles: [
      "The nervous system adapts.",
      "Behavior is downstream of state.",
    ],
    loopStage: "Adaptation",
    loopHighlight: "adaptation",
    flowHighlight: "state",
    frameworkStep: "04 · Retrain the Default",
    environment: {
      nervousSystemState:
        "Your self-reported emotional, behavioural, and physical patterns are consistent with reasonable baseline function and a regulatory range that can become more reliable through repeated matched inputs.",
      priorities: [
        "Widening the window of tolerance",
        "Strengthening awareness of internal state cues",
        "Replacing higher-cost regulators with lower-cost ones",
        "Making regulation the default rather than the recovery",
      ],
      behavioralOutputs: [
        "Old stress patterns re-appear under new load",
        "Intermittent scrolling / avoidance despite better inputs",
        "Progress that feels contingent on effort",
        "Regulation available, but not automatic",
      ],
      recoveryChallenges: [
        "Regulation may require attention rather than time",
        "Small dysregulations can still cascade if unmet",
        "Improvements may feel invisible before they feel present",
      ],
      whyThisMatters:
        "At this point, behaviour change becomes cheaper by widening the self-reported set-point itself. Educational options that shape regulation, environment and interoception may update the state the system defaults to — after which behaviours often reorganise with less effort.",
    },
    feelings: [
      "Good days that don't quite hold",
      "Progress that feels effortful rather than automatic",
      "Occasional slides into old patterns under load",
      "Ordinary regulation, without ease",
      "Uncertainty about whether the gains are real",
    ],
    inputs: [
      "zone2-aerobic",
      "environmental-design",
      "interoceptive-practice",
      "behavioral-substitutions",
    ],
    applications: [
      { label: "Habit & Behavior Change", to: "/applications/habit-change" },
      { label: "Anxiety & Regulation", to: "/applications/anxiety-regulation" },
    ],
    programs: [
      { label: "Guided Implementation", to: "/programs/guided-implementation" },
      {
        label: "Professional Consultation",
        to: "/programs/professional-consultation",
      },
    ],
  },
  "growth-performance": {
    key: "growth-performance",
    title: "Growth & Performance",
    subtitle: "The system is ready to raise its ceiling.",
    body: "Your responses describe strong state, inputs, and capacity. The next priority is progressive challenge, performance, and recovery that can support a higher sustainable baseline.",
    principles: [
      "Intentional inputs create new adaptations.",
      "State enables output.",
    ],
    loopStage: "New Adaptation",
    loopHighlight: "new-adaptation",
    flowHighlight: "baseline",
    frameworkStep: "04 · Retrain the Default (ceiling expansion)",
    environment: {
      nervousSystemState:
        "Your self-reported patterns are consistent with a relatively regulated range that can recruit activation for demand and return toward recovery, with room to expand performance capacity.",
      priorities: [
        "Expanding what the system can hold under load",
        "Preserving recovery quality as demand rises",
        "Refining awareness of internal cues near the edges of tolerance",
        "Structured progression without outrunning recovery",
      ],
      behavioralOutputs: [
        "Consistent engagement with wanted work",
        "Faster return to baseline after demand",
        "Attention available under pressure",
        "Regulation defaults, not effortful",
      ],
      recoveryChallenges: [
        "Ambition can outrun recovery signal — deloads matter more here, not less",
        "Ceiling expansion produces new fatigue patterns to interpret",
        "Under-recovering at this level is often invisible until it isn't",
      ],
      whyThisMatters:
        "At this stage educational options emphasise precision: matching load to readiness you can observe, protecting the recovery surface, and expressing capacity without paying for it in state. Topics such as stress-hormone rhythm, recovery trends, sleep quality and circadian consistency are often used as educational instruments — not as values measured by this assessment.",
    },
    feelings: [
      "Wanting more from a system that already delivers",
      "Ambition without accompanying dysregulation",
      "Curiosity about the ceiling, not just the baseline",
      "Regulation available even in demanding weeks",
      "A tolerance for load that surprises you",
    ],
    inputs: [
      "hrv-guided-training",
      "recovery-protocols",
      "attention-training",
      "deload-cadence",
    ],
    applications: [
      { label: "Performance", to: "/applications/performance" },
      { label: "Habit & Behavior Change", to: "/applications/habit-change" },
    ],
    programs: [
      { label: "Guided Implementation", to: "/programs/guided-implementation" },
      {
        label: "Professional Consultation",
        to: "/programs/professional-consultation",
      },
    ],
  },
};

/** Resolve an archetype's input keys to full input objects. */
export function resolveInputs(archetype) {
  return (archetype.inputs || []).map((k) => INPUT_LIBRARY[k]).filter(Boolean);
}
